import { remove } from 'lodash';
import {
  PriceObject,
  Promotion,
  TotalPriceObject,
  ShoppingCartProps,
  ProductOffering,
  ModuleObject,
} from '../types/Module';
import {
  IVA,
  PROMOTION_NAME_TEXT_ID,
  TERMS_AND_CONDITIONS_TEXT_ID,
  TITULO_PRODUCTOS,
  TITULO_CONDICIONES_PARTICULARES,
  TEXTO_CONDICIONES_PARTICULARES,
  LINK_CONDICIONES_PARTICULARES,
  TEXTO_LINK_CONDICIONES_PARTICULARES,
  TEXTO_INICIAL_TERMINOS_PROMOCIONES,
} from './textConstants';

export const sortModules = (
  moduleA: ModuleObject,
  moduleB: ModuleObject,
): number => moduleA.moduleOrder - moduleB.moduleOrder;

export const sortSubcategories = (
  moduleA: ModuleObject,
  moduleB: ModuleObject,
): number => moduleA.subCategoryOrder - moduleB.subCategoryOrder;

export const getIVATotalPrice = (
  prices: TotalPriceObject[],
): TotalPriceObject => {
  return prices.find((price) => price.taxName === IVA);
};
export const getIVAPrice = (prices: PriceObject[]): PriceObject => {
  return prices.find((price) => price.taxName === IVA);
};
const getPromotionText = (productPromotion: Promotion) => {
  if (!productPromotion) {
    return '';
  }
  if (!productPromotion.texts) {
    return '';
  }
  const promotionName = productPromotion.texts.find(
    (textObj) => textObj.typeId === PROMOTION_NAME_TEXT_ID,
  );
  if (!promotionName || !promotionName.text) {
    return '';
  }
  return promotionName.text;
};

const getPromotionIvaPrice = (productPromotion: Promotion) => {
  if (!productPromotion || !productPromotion.prices) {
    return undefined;
  }
  const ivaPrice = getIVAPrice(productPromotion.prices);
  if (
    !ivaPrice ||
    !ivaPrice.recurringCharges ||
    ivaPrice.recurringCharges.price === null ||
    ivaPrice.recurringCharges.price === undefined
  ) {
    return undefined;
  }
  return ivaPrice.recurringCharges.price;
};

export const getPromotionValues = (
  productPromotion: Promotion,
): {
  promotionText: string;
  hasPromotion: boolean;
  promotionPrice: number;
} => {
  const promotionText = getPromotionText(productPromotion);
  return {
    promotionText,
    hasPromotion: !!productPromotion,
    promotionPrice: getPromotionIvaPrice(productPromotion),
  };
};

export const getMappedModuleProduct = (
  moduleX: ModuleObject,
): {
  POId: string;
  text: string;
} => {
  const { texts, id } = moduleX;
  return {
    POId: id,
    text: (texts && texts.find((textObj) => textObj.typeId === '1').text) || '',
  };
};

const removeModulesWithOrder0 = (module: ModuleObject) => {
  return module.moduleOrder === 0 && module.subCategoryOrder === 0;
};

export const mapModulesToShoppingCartProps = (
  modulos: ModuleObject[],
  totalPrice: TotalPriceObject,
  title = '',
): ShoppingCartProps => {
  const { recurringCharges } = { ...totalPrice };
  const removedModules = remove(modulos, removeModulesWithOrder0);
  const shoppingCartProducts = modulos
    .sort(sortSubcategories)
    .sort(sortModules)
    .concat(removedModules)
    .map((moduleX) => {
      const { prices, promotions } = moduleX;
      const [productPromotion] = promotions;
      const mappedProductOffering = getMappedModuleProduct(moduleX);
      const {
        promotionText,
        hasPromotion,
        promotionPrice,
      } = getPromotionValues(productPromotion);
      const productPrice = getIVAPrice(prices).recurringCharges.price;

      return {
        productOffering: mappedProductOffering,
        priceBeforeDiscounts: hasPromotion ? productPrice : undefined,
        finalPrice: hasPromotion ? promotionPrice : productPrice,
        promotionText: hasPromotion ? promotionText : undefined,
      };
    });

  return {
    charges: {
      recurringCharges: {
        chargesGroupTitle: title,
        products: shoppingCartProducts,
        groupPrice:
          recurringCharges.finalPrice || recurringCharges.priceBeforeDiscounts,
      },
    },
  };
};

const getPromotionTerms = (productPromotion: Promotion) => {
  if (!productPromotion || !productPromotion.texts) {
    return '';
  }
  const termsText = productPromotion.texts.find(
    (textObj) => textObj.typeId === TERMS_AND_CONDITIONS_TEXT_ID,
  );
  if (!termsText || !termsText.text) {
    return '';
  }
  return termsText.text;
};

export const mapModulesToTerms = (modulos: ModuleObject[]): string => {
  return modulos
    .map((moduloX) => {
      const [productPromotion] = moduloX.promotions;
      return getPromotionTerms(productPromotion);
    })
    .filter((promotionTerms) => {
      return !!promotionTerms;
    })
    .reduce(
      (allTermsAndConditions, promotionTerms) =>
        `${allTermsAndConditions}<p>${promotionTerms}</p><br/>`,
      `<h3>${TITULO_PRODUCTOS}</h3>`,
    )
    .concat(
      `
    <h3>${TITULO_CONDICIONES_PARTICULARES}</h3>
    <p>
    ${TEXTO_CONDICIONES_PARTICULARES}
    <a href=${LINK_CONDICIONES_PARTICULARES} target="_blank">
    ${TEXTO_LINK_CONDICIONES_PARTICULARES}
    </a>
    </p>`,
    );
};

export const mapProductOfferingToTerms = (
  productOfferings: ProductOffering[],
): string => {
  return productOfferings
    .map((productOffering) => productOffering.provisionalAgreement)
    .filter(
      (provisionalAgreement) =>
        provisionalAgreement &&
        provisionalAgreement.webFormatDocument &&
        provisionalAgreement.name,
    )
    .map((provisionalAgreement) => {
      const { webFormatDocument } = provisionalAgreement;
      if (!webFormatDocument.startsWith('http')) {
        const newWebFormatDocument = `https://${webFormatDocument}`;
        return {
          ...provisionalAgreement,
          webFormatDocument: newWebFormatDocument,
        };
      }
      return provisionalAgreement;
    })
    .reduce(
      (allTermsAndConditions, provisionalAgreement) => `${allTermsAndConditions}
    <div id="download-terms-block">
    <a href=${provisionalAgreement.webFormatDocument} target="_blank" download=${provisionalAgreement.name} >
    <button class="p-1 button buttonNew--green_reverse-gp">Descargar</button>
    </a> 
    <div>${provisionalAgreement.name}</div>
    </div>`,
      `<div id="download-terms-block">${TEXTO_INICIAL_TERMINOS_PROMOCIONES}</div>`,
    );
};
